import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  Edit, 
  Shield,
  CheckCircle,
  XCircle,
  Search,
  UserPlus,
  Eye,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Mail,
  AlertTriangle,
  User
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedCompletion, setSelectedCompletion] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    employee_id: ''
  });

  useEffect(() => {
    if (hasRole('admin')) {
      fetchUsers();
    }
  }, [hasRole]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update user
        await axios.put(`${API}/users/${editingUser.id}`, {
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
          employee_id: formData.employee_id
        });
        toast.success('User updated successfully');
      } else {
        // Create new user
        await axios.post(`${API}/auth/register`, formData);
        toast.success('User created successfully');
      }
      
      fetchUsers();
      resetForm();
      setIsCreateModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'employee',
      employee_id: ''
    });
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setIsViewModalOpen(true);
  };

  const getProfileCompletion = (user) => {
    const fields = [
      'full_name', 'email', 'phone_number', 'department', 'job_title', 
      'employee_id', 'date_of_birth', 'address', 'emergency_contact_name', 
      'emergency_contact_phone', 'bio'
    ];
    
    const filledFields = fields.filter(field => {
      const value = user[field];
      return value && String(value).trim() !== '';
    });
    
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
      employee_id: user.employee_id || ''
    });
    setIsCreateModalOpen(true);
  };

  const handleDeactivate = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !isActive });
      toast.success(`User ${isActive ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.department && user.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.job_title && user.job_title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    
    const profileCompletion = getProfileCompletion(user);
    const matchesCompletion = 
      selectedCompletion === 'all' ||
      (selectedCompletion === 'complete' && profileCompletion >= 80) ||
      (selectedCompletion === 'incomplete' && profileCompletion < 80);
    
    return matchesSearch && matchesRole && matchesCompletion;
  });

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      employee: 'bg-blue-100 text-blue-800 border-blue-200',
      auditor: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    
    return (
      <Badge variant="outline" className={colors[role] || 'bg-gray-100 text-gray-800 border-gray-200'}>
        {role}
      </Badge>
    );
  };

  if (!hasRole('admin')) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">Only administrators can access user management.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">Manage user accounts and permissions</p>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
          setIsCreateModalOpen(open);
          if (!open) {
            setEditingUser(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2" data-testid="create-user-button">
              <UserPlus className="h-4 w-4" />
              <span>Add User</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Edit User' : 'Create New User'}
              </DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? 'Update user information and permissions.'
                  : 'Add a new user to the system with appropriate role and permissions.'
                }
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {!editingUser && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="user@company.com"
                      data-testid="user-email-input"
                    />
                  </div>
                )}
                
                {!editingUser && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Enter password"
                      data-testid="user-password-input"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder="John Doe"
                    data-testid="user-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="auditor">Auditor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee ID {!editingUser && '(Optional)'}</Label>
                  <Input
                    id="employee_id"
                    type="text"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                    placeholder="e.g., EMP001, A123"
                    data-testid="user-employee-id-input"
                  />
                  <p className="text-xs text-gray-500">
                    {editingUser 
                      ? 'Unique identifier for the employee. Only administrators can modify this.'
                      : 'Unique identifier for the employee. Once set, only administrators can modify this.'
                    }
                  </p>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-user-button">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {users.filter(u => u.is_active).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Administrators</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {users.filter(u => u.role === 'admin').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Complete Profiles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {users.filter(u => getProfileCompletion(u) >= 80).length}
            </div>
            <p className="text-xs text-gray-500 mt-1">≥ 80% complete</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Incomplete Profiles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {users.filter(u => getProfileCompletion(u) < 80).length}
            </div>
            <p className="text-xs text-gray-500 mt-1">&lt; 80% complete</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg. Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {users.length > 0 
                ? Math.round(users.reduce((acc, u) => acc + getProfileCompletion(u), 0) / users.length)
                : 0}%
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div 
                className="bg-blue-600 h-1.5 rounded-full" 
                style={{
                  width: `${users.length > 0 
                    ? Math.round(users.reduce((acc, u) => acc + getProfileCompletion(u), 0) / users.length)
                    : 0}%`
                }}
              ></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            User List
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, department, or job title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
            </div>
            
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="auditor">Auditor</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedCompletion} onValueChange={setSelectedCompletion}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Profile completion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Profiles</SelectItem>
                <SelectItem value="complete">Complete (≥80%)</SelectItem>
                <SelectItem value="incomplete">Incomplete (&lt;80%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Users Table */}
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
                <p className="text-gray-600">
                  {users.length === 0 
                    ? 'No users have been created yet.'
                    : 'No users match your current filters.'
                  }
                </p>
              </div>
            ) : (
              filteredUsers.map((user) => {
                const profileCompletion = getProfileCompletion(user);
                return (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{user.full_name}</h3>
                        <p className="text-sm text-gray-600 truncate">{user.email}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          {user.department && (
                            <span className="text-xs text-gray-500">{user.department}</span>
                          )}
                          {user.job_title && (
                            <>
                              {user.department && <span className="text-xs text-gray-400">•</span>}
                              <span className="text-xs text-gray-500">{user.job_title}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-2">
                          {getRoleBadge(user.role)}
                          {user.is_active ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200 text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Profile Completion */}
                    <div className="flex flex-col items-end space-y-2 min-w-0 w-32">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">{profileCompletion}%</span>
                        {profileCompletion >= 80 ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                        )}
                      </div>
                      <Progress 
                        value={profileCompletion} 
                        className={`w-24 h-2 ${profileCompletion >= 80 ? 'bg-green-100' : 'bg-orange-100'}`}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewUser(user)}
                        data-testid={`view-user-${user.id}`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(user)}
                        data-testid={`edit-user-${user.id}`}
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeactivate(user.id, user.is_active)}
                        className={user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        data-testid={`toggle-user-${user.id}`}
                      >
                        {user.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Details Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              User Details - {viewingUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Complete profile information and activity summary
            </DialogDescription>
          </DialogHeader>
          
          {viewingUser && (
            <div className="space-y-6">
              {/* Profile Overview */}
              <div className="flex items-start space-x-6 p-4 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-xl">
                    {viewingUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{viewingUser.full_name}</h3>
                  <p className="text-gray-600">{viewingUser.email}</p>
                  <div className="flex items-center space-x-3 mt-2">
                    {getRoleBadge(viewingUser.role)}
                    {viewingUser.is_active ? (
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{getProfileCompletion(viewingUser)}%</div>
                  <div className="text-sm text-gray-500">Profile Complete</div>
                  <Progress value={getProfileCompletion(viewingUser)} className="w-24 h-2 mt-2" />
                </div>
              </div>

              <Tabs defaultValue="profile" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="profile">Profile Information</TabsTrigger>
                  <TabsTrigger value="activity">Account Activity</TabsTrigger>
                </TabsList>
                
                <TabsContent value="profile" className="space-y-4">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Basic Information */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Basic Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="font-medium">{viewingUser.email}</p>
                          </div>
                        </div>
                        {viewingUser.phone_number && (
                          <div className="flex items-center space-x-3">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-sm text-gray-600">Phone</p>
                              <p className="font-medium">{viewingUser.phone_number}</p>
                            </div>
                          </div>
                        )}
                        {viewingUser.date_of_birth && (
                          <div className="flex items-center space-x-3">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-sm text-gray-600">Date of Birth</p>
                              <p className="font-medium">{new Date(viewingUser.date_of_birth).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Work Information */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center">
                          <Briefcase className="h-4 w-4 mr-2" />
                          Work Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {viewingUser.job_title && (
                          <div>
                            <p className="text-sm text-gray-600">Job Title</p>
                            <p className="font-medium">{viewingUser.job_title}</p>
                          </div>
                        )}
                        {viewingUser.department && (
                          <div>
                            <p className="text-sm text-gray-600">Department</p>
                            <p className="font-medium">{viewingUser.department}</p>
                          </div>
                        )}
                        {viewingUser.employee_id && (
                          <div>
                            <p className="text-sm text-gray-600">Employee ID</p>
                            <p className="font-medium">{viewingUser.employee_id}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Contact Information */}
                    {(viewingUser.address || viewingUser.emergency_contact_name) && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center">
                            <MapPin className="h-4 w-4 mr-2" />
                            Contact Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {viewingUser.address && (
                            <div>
                              <p className="text-sm text-gray-600">Address</p>
                              <p className="font-medium">{viewingUser.address}</p>
                            </div>
                          )}
                          {viewingUser.emergency_contact_name && (
                            <div>
                              <p className="text-sm text-gray-600">Emergency Contact</p>
                              <p className="font-medium">{viewingUser.emergency_contact_name}</p>
                              {viewingUser.emergency_contact_phone && (
                                <p className="text-sm text-gray-500">{viewingUser.emergency_contact_phone}</p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* About */}
                    {viewingUser.bio && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center">
                            <Users className="h-4 w-4 mr-2" />
                            About
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700">{viewingUser.bio}</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="activity" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Member Since</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg font-semibold">
                          {viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Last Login</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg font-semibold">
                          {viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleDateString() : 'Never'}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Account Status</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-lg font-semibold ${viewingUser.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {viewingUser.is_active ? 'Active' : 'Inactive'}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Alert className={`${getProfileCompletion(viewingUser) >= 80 ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
                    {getProfileCompletion(viewingUser) >= 80 ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    )}
                    <AlertDescription className={getProfileCompletion(viewingUser) >= 80 ? 'text-green-700' : 'text-orange-700'}>
                      {getProfileCompletion(viewingUser) >= 80 
                        ? 'This user has completed their profile information.' 
                        : `This user's profile is ${getProfileCompletion(viewingUser)}% complete. Encourage them to fill out their remaining profile information.`
                      }
                    </AlertDescription>
                  </Alert>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Security Notice */}
      <Alert className="border-amber-200 bg-amber-50">
        <Shield className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700">
          <strong>Security Note:</strong> User management actions are logged for audit purposes. 
          All password changes require the user to log in again. Deactivated users cannot access the system.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default UserManagement;